import { Box, Transition, MantineTransition } from '@mantine/core';
import { forwardRef } from 'react';
import { useReducedMotion } from '../hooks';

export type SlideDirection = 'up' | 'down' | 'left' | 'right';

export interface SlideInProps {
  children: React.ReactNode;
  visible?: boolean;
  direction?: SlideDirection;
  duration?: number;
  delay?: number;
  distance?: number | string;
  timingFunction?: string;
  onEnter?: () => void;
  onEntered?: () => void;
  onExit?: () => void;
  onExited?: () => void;
}

const getTransition = (direction: SlideDirection): MantineTransition => {
  switch (direction) {
    case 'up':
      return 'slide-up';
    case 'down':
      return 'slide-down';
    case 'left':
      return 'slide-left';
    case 'right':
      return 'slide-right';
    default:
      return 'slide-up';
  }
};

const SlideIn = forwardRef<HTMLDivElement, SlideInProps>(
  (
    {
      children,
      visible = true,
      direction = 'up',
      duration = 300,
      delay = 0,
      timingFunction = 'ease-out',
      onEnter,
      onEntered,
      onExit,
      onExited,
    },
    ref
  ) => {
    const reducedMotion = useReducedMotion();
    const actualDuration = reducedMotion ? 0 : duration;

    return (
      <Transition
        mounted={visible}
        transition={getTransition(direction)}
        duration={actualDuration}
        timingFunction={timingFunction}
        onEnter={onEnter}
        onEntered={onEntered}
        onExit={onExit}
        onExited={onExited}
      >
        {(styles) => (
          <Box
            ref={ref}
            style={{
              ...styles,
              transitionDelay: `${delay}ms`,
            }}
          >
            {children}
          </Box>
        )}
      </Transition>
    );
  }
);

SlideIn.displayName = 'SlideIn';

export default SlideIn;
